/**
 * formatParagraphsExecutor — `format_paragraphs` : style nommé, alignement,
 * retraits, espacement, interligne, bordure et fond de paragraphes entiers,
 * fond et cadre de leur cellule de tableau, et leur remise à zéro, sur une
 * liste, une plage ou tout l'acte (#1866, #1867, spec #1858 ; fonds :
 * 2026-10-11 ; cadre de case : 2026-10-12).
 *
 * Les règles communes aux écritures de forme — écriture directe, cible résolue
 * au fichier exporté, relecture clôturée sur la révision, ce qui compte comme
 * une écriture — sont dites une fois, dans l'en-tête de `ecritureDeForme.ts`.
 * Ce que chaque réglage devient pour l'éditeur vit dans
 * `reglagesDeParagraphe.ts`, avec ses mesures ; ce qu'une remise à zéro écrit
 * et laisse, dans `remiseAZero.ts` ; l'enchaînement des écritures sur la liste
 * des paragraphes, dans `travauxDeParagraphe.ts` ; le reçu est décidé par
 * `recuDEcritureDeForme.ts`.
 *
 * ## Deux manières d'écrire, dans le même appel
 *
 * - **L'alignement part en plan atomique** (`mutations.apply`, une étape
 *   `format.apply` par paragraphe, 200 étapes par plan) : une entrée
 *   d'historique pour tout le lot, donc un Ctrl+Z.
 * - **Style nommé, retraits, espacement, interligne, bordure et fond n'ont pas
 *   d'étape de plan** (`mesure-forme-en-lot`, 2026-10-01 : « plan engine does
 *   not support step op » ; `mesure-socle-styles-nommes`, b ;
 *   `mesure-traits-et-fonds`, F12) : une écriture par paragraphe et par
 *   famille — par CÔTÉ pour une bordure, donc quatre pour un encadré —, chacune
 *   une entrée d'historique. Le reçu en donne le nombre (`format.writes`), que
 *   `undo_changes` défait.
 *
 * ## Le fond et le cadre d'une cellule de tableau
 *
 * `cellShading` et `cellBorder` écrivent la CELLULE qui contient chaque
 * paragraphe visé, pas le paragraphe : l'agent lit le fond et les bordures
 * d'une cellule sur la ligne de cette cellule et n'a en main que les
 * identifiants de ses paragraphes. Trois conséquences, pour l'un comme pour
 * l'autre :
 *
 * - un paragraphe visé hors d'une cellule, ou dans une cellule que l'éditeur
 *   ne sait pas nommer, fait refuser l'appel avant toute écriture
 *   (`FORMAT_TARGET_NOT_A_TABLE_CELL`) : il n'y a pas de cellule à écrire, et
 *   écrire le paragraphe à la place serait un autre réglage (`shading`,
 *   `border`) ;
 * - plusieurs paragraphes visés d'une même cellule valent UNE écriture, portée
 *   par le premier ; tous sont jugés, sur le fond ou le cadre relu de leur
 *   cellule ;
 * - l'écriture n'est soumise que si le fichier montre qu'elle change la
 *   cellule : l'éditeur rend un succès sans rien écrire pour un fond ou des
 *   côtés déjà en place (`reglagesDeParagraphe.ts`, « Le fond », « Le cadre
 *   d'une case de tableau »).
 *
 * Les surfaces des fonds et du cadre de case ne sont exigées que d'un appel
 * qui les emploie : les exiger du verbe le ferait disparaître d'un éditeur qui
 * ne les expose pas.
 *
 * ## Un style nommé, seul ou avec d'autres réglages
 *
 * `styleId` pose un style de paragraphe que le FICHIER de l'acte définit —
 * celui d'un Word importé comme ceux du gabarit Hector. Un style qu'il ne
 * définit pas est refusé avant toute écriture, les styles définis nommés
 * (`FORMAT_STYLE_NOT_FOUND`). Le style ne touche pas à ce que le paragraphe et
 * son texte posent à la main ; un paragraphe qui le porte déjà n'est pas écrit.
 *
 * Avec d'autres réglages dans le même appel, le résultat est celui du style
 * posé D'ABORD, puis des réglages posés à la main par-dessus lui :
 *
 * 1. chaque réglage se décide sur ce que le paragraphe montrera sous le style
 *    demandé (`reglagesDeParagraphe.ts`, `sousLeStyle`), pas sur ce qu'il
 *    montre avant ;
 * 2. dans la série par paragraphe, le style est écrit avant les retraits,
 *    l'espacement et la bordure (`travauxDeParagraphe.ts`,
 *    `ecrituresHorsPlanDuParagraphe`) ;
 * 3. l'alignement, lui, part AVANT le style : il est en plan, et un plan ne
 *    peut pas suivre une écriture par paragraphe (`ecritureDeForme.ts`, « Ce
 *    que la révision garde »). Le fichier final est le même, la pose du style
 *    gardant l'alignement posé à la main.
 *
 * Le style demandé est posé tel quel : s'il numérote, le paragraphe est
 * numéroté ; un paragraphe que son style d'avant numérotait ne porte plus ce
 * style (non mesuré : ce que l'éditeur peint alors).
 *
 * Une remise à zéro (`reset`) suit le même ordre : la forme du texte part en
 * plans atomiques, puis chaque paragraphe reçoit une écriture. Elle ne se
 * combine avec aucun autre réglage (contrat partagé, `actionCatalog.ts`). Un
 * paragraphe dont l'éditeur refuse l'étape de texte est sauté de même pour tout
 * l'appel ; les étapes de texte que l'éditeur risque de refuser partent seules.
 *
 * Les plans d'alignement passent d'abord : tous sont soumis à l'aperçu avant la
 * première écriture, si bien qu'une étape que l'éditeur refuserait est connue
 * document intact. Un paragraphe dont l'éditeur refuse l'étape d'alignement, à
 * l'aperçu ou à l'application, est SAUTÉ pour tout l'appel — ni retrait ni
 * espacement n'y sont tentés — et nommé au reçu avec sa raison
 * (`format.skipped`) ; le reste de la cible est écrit (`ecritureDeForme.ts`,
 * « Quand l'éditeur refuse un lot »).
 *
 * ## Un paragraphe dont la forme attend une décision
 *
 * Un paragraphe qui porte un changement de forme de paragraphe EN ATTENTE
 * (`w:pPrChange`) est écrit comme les autres, et le reçu le nomme : la règle,
 * sa mesure et la fonction qui la porte sont dans `ecritureDeForme.ts`. Une
 * remise à zéro, elle, EFFACERAIT cette révision : elle laisse le paragraphe
 * (`remiseAZero.ts`).
 *
 * ## Ce qui est mesuré, et dont le code dépend
 *
 * Sonde `tests/ws-mocked/mesure-format-paragraphs.spec.ts` (SuperDoc 2.15.1,
 * 2026-10-02, éditeur en mode suggestion, appels en direct) :
 *
 * - Aucune de ces écritures ne crée de révision sous suivi activé (A1 à E9 :
 *   0 révision, aucun `w:pPrChange`).
 * - **Un paragraphe sans texte s'écrit comme un autre** (A4, R9, E8), au
 *   contraire d'une forme de texte : il n'est pas écarté de la cible.
 * - **L'identifiant décide seul du paragraphe visé**, pour une écriture par
 *   paragraphe (Q1) comme pour une étape de plan (P1) : un titre adressé comme
 *   paragraphe est écrit. L'exécuteur ne lit donc pas la liste des blocs.
 * - **L'historique de l'éditeur garde 100 entrées** (H1 : 130 écritures, 100
 *   annulations abouties, les 30 premières écritures restent). Au-delà de 100
 *   écritures dans un appel, les premières ne se défont plus : le reçu le
 *   compte (`format.writesNotUndoable`).
 * - Coût (G1, acte de 1 700 paragraphes, deux passes) : 57 à 103 ms par
 *   écriture en médiane, 112 à 242 ms en moyenne avec les exports de
 *   l'enregistrement automatique ; 12,5 à 12,8 s pour un plan d'alignement de
 *   200 paragraphes ; 15 ms par annulation. Sur un acte de 150 paragraphes :
 *   8 ms par écriture.
 *
 * ## Une écriture interrompue
 *
 * La série s'arrête au premier refus de l'éditeur (le refus passager d'un
 * export est déjà réessayé par `ctx.mutate`, `refusPassager.ts`). Ce qui est
 * écrit le reste ; le reçu compte les paragraphes entièrement écrits, ceux qui
 * ne le sont qu'en partie — avec ce qui est écrit, famille par famille — et
 * ceux qui ne le sont pas, et aucun réglage n'y est dit conforme pour la
 * demande entière. Un paragraphe dont une écriture manque n'est pas jugé, même
 * pour celles qu'il a reçues : la relecture ne répond que de ce qui est complet.
 *
 * ## Une écriture qu'on ne peut pas faire sans changer autre chose
 *
 * Quand `w:ind` ou `w:spacing` d'un paragraphe porte, à la main, des attributs
 * que l'éditeur efface en écrivant l'élément (`reglagesDeParagraphe.ts`), CETTE
 * écriture n'est pas tentée ; l'alignement et l'autre écriture du paragraphe le
 * sont. Le paragraphe sort des comptes de paragraphes écrits : il est compté à
 * part (`format.skipped`, `attributes_would_be_lost`), nommé par le reçu, et
 * chaque réglage laissé porte cette raison dans `format.settings`. L'appel
 * n'est refusé d'avance que s'il ne reste rien à écrire nulle part.
 *
 * ## Hors périmètre
 *
 * Dans un en-tête, un pied ou une note, les paragraphes de cellule de tableau
 * ne sont pas écrits (comptés `table_cell`, reçu `partial`).
 *
 * Dans le corps, une cellule nommée (`ids`) s'écrit et se relit comme un autre
 * paragraphe (§16 de `superdoc-document-api.md`). « Tout l'acte » et une plage,
 * eux, laissent les cellules à leur tableau : comptées (`table_cell_by_ids_only`),
 * reçu `ok` — la règle et son pourquoi sont à `PorteeDansLesTableaux`
 * (`cibleDeParagraphes.ts`).
 */

import { lieuDeLaCible, type CanvasActionInput } from '@hector/shared-types';
import type { CanvasActionReceipt } from './actionReceipt';
import { resoudreLaCibleDeParagraphes, type ParagrapheCible } from './cibleDeParagraphes';
import { ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe, ouvrirLEcritureDeForme, type EcritureDeFormeOuverte } from './ecritureDeForme';
import { blocDeLEcriture, direLeLieu } from './horsDuCorps';
import { RELANCE_LE_PEINTRE } from './peintreDeLEditeur';
import { decrireLesNonResolus, direLesHorsDePortee, nommerLesParagraphes, recuDeLEcritureDeForme } from './recuDEcritureDeForme';
import {
  demandeDeParagraphes,
  ecrituresDeLaDemande,
  type DemandeDeParagraphes,
  type EcritureEmpechee,
  type EcrituresDeParagraphe,
  type FamilleDEcriture,
} from './reglagesDeParagraphe';
import { refusDuStyleAbsent } from './stylesPourLAgent';
import { bilanDeLaRemiseAZero, ecrituresDeRemiseAZero, INLINE_DE_REMISE_A_ZERO, type EcrituresDeRemiseAZero } from './remiseAZero';
import type { RaisonDeSaut } from './repliDeLot';
import type { CanvasActionContext } from './textActionExecutors';
import {
  compterLAvancement,
  direLAvancement,
  ecrireLaSerie,
  ecrireParPlans,
  ecritsSurUneRevisionDeParagraphe,
  ecrituresHorsPlanDuParagraphe,
  estEntier,
  type EcritureHorsPlan,
  type FamilleDeTravail,
  type MethodeProvider,
  type SurfaceDeParagraphe,
  type SurfacesHorsPlan,
  type Travail,
} from './travauxDeParagraphe';

type FormatParagraphsInput = Extract<CanvasActionInput, { action: 'format_paragraphs' }>;

/** Les écritures par paragraphe, mesurées présentes (`mesure-format-paragraphs`, `mesure-format-paragraphs-bordure-reset`, `mesure-socle-styles-nommes`). */
interface SurfacesDeParagraphe {
  tables?: SurfacesHorsPlan['tables'];
  styles?: { paragraph?: SurfacesHorsPlan['styles'] };
  format?: {
    paragraph?: SurfaceDeParagraphe & {
      resetDirectFormatting?: MethodeProvider;
      setFlowOptions?: MethodeProvider;
      setOutlineLevel?: MethodeProvider;
    };
  };
}

const INTENTION = 'format_paragraphs';

/** Un paragraphe de la cible, ce qu'il doit recevoir, et ce qu'il a reçu. */
interface MiseEnForme extends Travail {
  ecritures: EcrituresDeParagraphe;
  faites: Set<FamilleDEcriture>;
  /** Les écritures écartées d'avance parce qu'elles effaceraient des attributs posés à la main : `ecritures` ne les contient pas. */
  empechees: EcritureEmpechee[];
}

const FAMILLES: ReadonlyArray<FamilleDeTravail<MiseEnForme> & { famille: FamilleDEcriture; nom: string }> = [
  { famille: 'style', nom: 'named style', prevue: ({ ecritures }) => ecritures.style !== undefined },
  { famille: 'alignement', nom: 'alignment', prevue: ({ ecritures }) => ecritures.alignement !== undefined },
  { famille: 'retraits', nom: 'indents', prevue: ({ ecritures }) => ecritures.retraits !== undefined },
  { famille: 'espacement', nom: 'spacing and line spacing', prevue: ({ ecritures }) => ecritures.espacement !== undefined },
  { famille: 'bordure', nom: 'border', prevue: ({ ecritures }) => ecritures.bordure !== undefined },
  { famille: 'fond', nom: 'shading', prevue: ({ ecritures }) => ecritures.fond !== undefined },
  { famille: 'fondDeCellule', nom: 'table-cell shading', prevue: ({ ecritures }) => ecritures.fondDeCellule !== undefined },
  { famille: 'cadreDeCase', nom: 'table-cell border', prevue: ({ ecritures }) => ecritures.cadreDeCase !== undefined },
];

/** Une écriture ouverte dont la cible désigne au moins un paragraphe du corps. */
interface EcritureSurLaCible {
  ecriture: EcritureDeFormeOuverte;
  /** Les réglages demandés, avec le style nommé tel que le fichier le définit. */
  demande: DemandeDeParagraphes;
  styles: SurfacesHorsPlan['styles'];
  paragraphe: NonNullable<SurfacesDeParagraphe['format']>['paragraph'];
  tables: SurfacesHorsPlan['tables'];
  resolue: ReturnType<typeof resoudreLaCibleDeParagraphes>;
  /** Les paragraphes de la cible, dans l'ordre du document. */
  dansLOrdre: ParagrapheCible[];
}

/** Ouvre l'écriture et résout la cible ; rend le reçu du refus quand l'une ou l'autre fait défaut, rien n'étant écrit. */
async function ouvrirSurLaCible(ctx: CanvasActionContext, input: FormatParagraphsInput): Promise<EcritureSurLaCible | { refus: CanvasActionReceipt }> {
  // Les méthodes sont appelées SUR leur surface, jamais détachées : rien ne
  // garantit qu'une méthode du fournisseur reste liée une fois extraite.
  const doc = ctx.doc as CanvasActionContext['doc'] & SurfacesDeParagraphe;
  const [styles, paragraphe, tables] = [doc.styles?.paragraph, doc.format?.paragraph, doc.tables];
  const lieu = lieuDeLaCible(input.target);
  const ecriture = await ouvrirLEcritureDeForme(ctx, INTENTION, {
    'styles.paragraph.setStyleRef': styles?.setStyleRef,
    'format.paragraph.setIndentation': paragraphe?.setIndentation,
    'format.paragraph.setSpacing': paragraphe?.setSpacing,
    'format.paragraph.setBorder': paragraphe?.setBorder,
    'format.paragraph.clearBorder': paragraphe?.clearBorder,
    'format.paragraph.resetDirectFormatting': paragraphe?.resetDirectFormatting,
    'format.paragraph.setFlowOptions': paragraphe?.setFlowOptions,
    'format.paragraph.setOutlineLevel': paragraphe?.setOutlineLevel,
    ...(input.shading === undefined ? {} : { 'format.paragraph.setShading': paragraphe?.setShading, 'format.paragraph.clearShading': paragraphe?.clearShading }),
    ...(input.cellShading === undefined ? {} : { 'tables.setShading': tables?.setShading, 'tables.clearShading': tables?.clearShading }),
    ...(input.cellBorder === undefined ? {} : { 'tables.setBorders': tables?.setBorders }),
  }, lieu);
  if ('refus' in ecriture) return ecriture;
  const { acte, refuser } = ecriture;

  const demande = demandeDeParagraphes(input, acte.stylesDeParagraphe);
  if ('styleAbsent' in demande) {
    return { refus: refuser({ code: 'FORMAT_STYLE_NOT_FOUND', message: refusDuStyleAbsent(INTENTION, demande.styleAbsent, acte) }) };
  }

  const resolue = resoudreLaCibleDeParagraphes(acte, 'cellules_par_identifiants', input.target);
  if (input.target.kind === 'range' && resolue.nonResolus.length > 0) {
    return { refus: refuser({
      code: 'FORMAT_TARGET_NOT_FOUND',
      message: `format_paragraphs was refused before any write: range bound ${decrireLesNonResolus(resolue.nonResolus)}. `
        + 'Nothing was written: a range with a missing bound is never guessed. Use two nodeIds of paragraphs published by a read of this document (table cells included).',
    }) };
  }
  if (resolue.paragraphes.length === 0) {
    const couvert = [
      ...resolue.omis.map(({ reason, count }) => `${count} ${reason}`),
      ...(resolue.cellules.laissees === 0 ? [] : [`${resolue.cellules.laissees} table-cell paragraph(s), left to their table (target their ids only when the lawyer asked for the tables themselves)`]),
      ...(resolue.nonResolus.length === 0 ? [] : [`unresolved: ${decrireLesNonResolus(resolue.nonResolus)}`]),
    ];
    return { refus: refuser({
      code: 'FORMAT_TARGET_EMPTY',
      message: `format_paragraphs was refused before any write: the target designates no paragraph in ${direLeLieu(lieu)}`
        + `${couvert.length === 0 ? '' : ` (${couvert.join('; ')})`}. Nothing was written. `
        + (direLesHorsDePortee('format_paragraphs', resolue.omis, lieu) ?? ecriture.siLaCibleEstVide ?? 'Target paragraphs of the document body.'),
    }) };
  }
  // Une liste est rendue dans l'ordre demandé : les écritures, elles, suivent l'ordre du document.
  const dansLOrdre = [...resolue.paragraphes].sort((a, b) => a.rang - b.rang);
  const reglagesDeCellule = (['cellShading', 'cellBorder'] as const).filter((reglage) => demande.reglages[reglage] !== undefined);
  if (reglagesDeCellule.length > 0) {
    const refus = refusHorsCellule(acte, dansLOrdre, reglagesDeCellule);
    if (refus !== undefined) return { refus: refuser({ code: 'FORMAT_TARGET_NOT_A_TABLE_CELL', message: refus }) };
  }
  return { ecriture, demande, styles, paragraphe, tables, resolue, dansLOrdre };
}

/**
 * Pourquoi un réglage de cellule (`cellShading`, `cellBorder`) ne peut pas être
 * écrit sur cette cible ; `undefined` : chaque paragraphe visé est dans une
 * cellule que l'éditeur sait trouver.
 *
 * Une cellule se nomme d'après son premier paragraphe (`reglagesDeParagraphe.ts`,
 * « Le fond ») : sans lui, ou quand son identifiant est porté par deux
 * paragraphes de l'acte, rien ne dit quelle cellule l'éditeur écrirait.
 */
function refusHorsCellule(acte: EcritureDeFormeOuverte['acte'], vises: readonly ParagrapheCible[], reglages: ReadonlyArray<'cellShading' | 'cellBorder'>): string | undefined {
  const nommes = reglages.join(' and ');
  const horsCellule = vises.filter((vise) => vise.cellule === undefined);
  if (horsCellule.length > 0) {
    return `format_paragraphs ${nommes} was refused before any write: ${horsCellule.length} targeted paragraph(s) (${nommerLesParagraphes(horsCellule.map((vise) => vise.nodeId))}) `
      + `are not inside a table cell, so there is no cell to write. Nothing was written. ${nommes} set${reglages.length > 1 ? '' : 's'} the table cell that CONTAINS each targeted paragraph: `
      + 'target paragraphs of table cells by their ids (target {kind:"ids"}), without any body paragraph. For the background or the border of a paragraph itself, use "shading" or "border".';
  }
  const enDouble = new Set(acte.paragraphes.flatMap((lu) => lu.identifiantEnDouble ?? []));
  const sansNom = vises.filter(({ cellule }) => cellule === undefined || cellule.premierParagraphe === null || enDouble.has(cellule.premierParagraphe));
  if (sansNom.length === 0) return undefined;
  return `format_paragraphs ${nommes} was refused before any write: the table cell of ${sansNom.length} targeted paragraph(s) (${nommerLesParagraphes(sansNom.map((vise) => vise.nodeId))}) `
    + 'cannot be addressed in the editor — it opens with a nested table, or its first paragraph has no identifier or shares it with another paragraph. '
    + 'Nothing was written. Tell the lawyer the background and the border of that cell must be set in Word, and resend the call without these paragraphs for the other cells.';
}

/**
 * Met en forme les paragraphes que la cible désigne, en écriture directe, puis
 * relit le fichier exporté pour dire ce qui est réellement posé.
 *
 * Refusé avant toute écriture (`aborted`, cause nommée) tant que la cible, la
 * lecture du fichier ou l'aperçu d'un plan fait défaut ; ensuite, le reçu est
 * décidé par `recuDeLEcritureDeForme` sur les faits relevés ici.
 */
export async function executeFormatParagraphs(ctx: CanvasActionContext, input: FormatParagraphsInput): Promise<CanvasActionReceipt> {
  const ouverte = await ouvrirSurLaCible(ctx, input);
  if ('refus' in ouverte) return ouverte.refus;
  if (input.reset === true) return remettreAZero(ouverte);
  const { ecriture, demande, styles, paragraphe, tables, resolue, dansLOrdre } = ouverte;
  const { refuser } = ecriture;
  const { reglages } = demande;
  // Un changement de forme de paragraphe en attente : la règle commune des verbes de forme (`ecritureDeForme.ts`).
  const { aEcrire: ecrivables, surRevision, epargnes } = ecrireOuEpargnerLesRevisionsDeFormeDeParagraphe(dansLOrdre);
  const prevus = ecrivables.map((lu): MiseEnForme => ({ paragraphe: lu, ...ecrituresDeLaDemande(lu, demande), faites: new Set(), appliquees: new Set() }));
  // Une cellule n'a qu'un fond et qu'un cadre : de plusieurs paragraphes visés d'une même cellule, le premier porte l'écriture.
  for (const famille of ['fondDeCellule', 'cadreDeCase'] as const) {
    const cellulesPrevues = new Set<string>();
    for (const { ecritures } of prevus) {
      const cellule = ecritures[famille]?.cellule;
      if (cellule === undefined) continue;
      if (cellulesPrevues.has(cellule)) delete ecritures[famille];
      else cellulesPrevues.add(cellule);
    }
  }
  // Un paragraphe dont une écriture est écartée sort des comptes de paragraphes écrits : il est compté à part, et nommé.
  const laisses = prevus.filter((prevu) => prevu.empechees.length > 0);
  const gardes = prevus.filter((prevu) => prevu.empechees.length === 0);
  if (gardes.length === 0 && laisses.length > 0 && laisses.every((laisse) => Object.keys(laisse.ecritures).length === 0)) {
    return refuser({
      code: 'FORMAT_TARGET_NOT_WRITABLE',
      message: `format_paragraphs was refused before any write: the ${laisses.length} targeted paragraph(s) (${nommerLesParagraphes(laisses.map((laisse) => laisse.paragraphe.nodeId))}) `
        + `carry attributes set by hand that the editor cannot keep when it writes ${[...new Set(laisses.flatMap((laisse) => laisse.empechees.flatMap((empechee) => empechee.reglages)))].join(', ')} `
        + `(${[...new Set(laisses.flatMap((laisse) => laisse.empechees.flatMap((empechee) => empechee.attributs)))].join(', ')}): `
        + 'writing would change a setting nobody asked for, and nothing else in the request needed writing. Nothing was written; '
        + 'tell the lawyer these paragraphs keep their form for these settings. Alignment can still be set on them.',
    });
  }
  // Un paragraphe qui montre déjà tout ce qui est demandé n'est pas réécrit.
  const travaux = prevus.filter((travail) => Object.keys(travail.ecritures).length > 0);
  const horsPlan = (travail: MiseEnForme): EcritureHorsPlan[] => ecrituresHorsPlanDuParagraphe({ styles, paragraphe, tables }, travail.paragraphe, travail.ecritures);

  // ── L'alignement : une étape par paragraphe, en plans atomiques directs. Le paragraphe d'une étape que
  // l'éditeur refuse est sauté pour tout l'appel, avec la raison à redire.
  const plans = await ecrireParPlans(ecriture, travaux.filter((travail) => travail.ecritures.alignement !== undefined), {
    famille: 'alignement',
    versEtape: (travail) => ({ alignment: travail.ecritures.alignement }),
    nom: 'alignment',
    etiquette: 'alignement',
    dAutresEcrituresSuivent: travaux.some((travail) => horsPlan(travail).length > 0),
  });
  if ('refus' in plans) return plans.refus;

  // ── Style nommé, retraits, espacement, bordure, puis fonds et cadre de case : une écriture par paragraphe et
  // par famille — par côté pour la bordure, par cellule pour son fond et son cadre —, dans l'ordre du document.
  const serie = await ecrireLaSerie(ecriture, travaux, horsPlan);
  const cloture = await ecriture.clore();

  // Ce que l'éditeur refuse d'écrire, et ce qui est épargné d'avance, dans l'ordre du document.
  const sautes: Array<{ paragraphe: ParagrapheCible; raison: RaisonDeSaut }> = [
    ...epargnes.map((epargne) => ({ paragraphe: epargne, raison: { reason: 'pending_tracked_change' as const, epargne: true as const } })),
    ...prevus.flatMap((prevu) => (prevu.saute === undefined ? [] : [{ paragraphe: prevu.paragraphe, raison: prevu.saute }])),
  ].sort((a, b) => a.paragraphe.rang - b.paragraphe.rang);
  // Un paragraphe sauté est compté une fois, comme sauté : il sort des paragraphes laissés et des paragraphes jugés.
  const laissesEcrivables = laisses.filter((laisse) => laisse.saute === undefined);
  const cible = {
    paragraphes: gardes.filter((garde) => garde.saute === undefined).map((garde) => garde.paragraphe),
    omis: [...resolue.omis, ...(laissesEcrivables.length === 0 ? [] : [{ reason: 'attributes_would_be_lost' as const, count: laissesEcrivables.length }])],
    nonResolus: resolue.nonResolus,
    cellules: resolue.cellules,
  };

  // Toutes les familles de ce verbe écrivent une forme de paragraphe, sauf le fond et le cadre de cellule, qui écrivent la cellule.
  const ecritsSurRevision = ecritsSurUneRevisionDeParagraphe(travaux, surRevision, (_travail, famille) => famille !== 'fondDeCellule' && famille !== 'cadreDeCase');
  return recuDeLEcritureDeForme({
    verbe: INTENTION,
    reglages,
    cible,
    // Les comptes de paragraphes écrits ne prennent que les paragraphes dont aucune écriture n'est écartée.
    ...compterLAvancement(travaux.filter((travail) => travail.empechees.length === 0 && travail.saute === undefined), FAMILLES, direLAvancement(travaux, FAMILLES)),
    ecrituresPrevues: plans.plans + serie.prevues,
    sautes: sautes.map(({ paragraphe: saute, raison }) => ({ nodeId: saute.nodeId, ...raison })),
    ...(ecritsSurRevision.length === 0 ? {} : { surRevisionDeParagraphe: ecritsSurRevision }),
    ...(laissesEcrivables.length === 0 ? {} : {
      laisses: laissesEcrivables.map((laisse) => ({
        nodeId: laisse.paragraphe.nodeId,
        reglages: laisse.empechees.flatMap((empechee) => empechee.reglages),
        attributs: laisse.empechees.flatMap((empechee) => empechee.attributs),
        ecrit: estEntier(laisse, FAMILLES),
      })),
    }),
    ...cloture,
  });
}

/** Ce qu'une remise à zéro écrit sur un paragraphe : la forme de son texte, celle du paragraphe, puis la structure à redonner. */
type FamilleDeRemise = 'texte' | 'paragraphe' | 'structure';

/** Un paragraphe de la cible à remettre à zéro, ce qu'il doit recevoir, et ce qu'il a reçu. */
interface Remise extends Travail {
  aEcrire: EcrituresDeRemiseAZero;
  faites: Set<FamilleDeRemise>;
}

/** Les écritures qui redonnent la structure d'un paragraphe : une pour le saut de page avant, une pour le niveau de plan. */
const ecrituresDeStructure = (remise: Remise): number => Object.keys(remise.aEcrire.structure).length;

const FAMILLES_DE_REMISE: ReadonlyArray<FamilleDeTravail<Remise> & { famille: FamilleDeRemise }> = [
  { famille: 'texte', prevue: (remise) => remise.aEcrire.texte },
  { famille: 'paragraphe', prevue: (remise) => remise.aEcrire.paragraphe },
  // La structure n'est à redonner que si la remise à zéro du paragraphe l'efface.
  { famille: 'structure', prevue: (remise) => remise.aEcrire.paragraphe && ecrituresDeStructure(remise) > 0 },
];

/**
 * Remet à zéro les paragraphes de la cible (`reset`) : la forme directe du
 * texte par plans atomiques, puis celle de chaque paragraphe par une écriture,
 * suivie de sa structure redonnée (saut de page avant, niveau de plan).
 * Les règles, et les mesures dont elles viennent, sont dans `remiseAZero.ts`.
 */
async function remettreAZero({ ecriture, paragraphe, resolue, dansLOrdre }: EcritureSurLaCible): Promise<CanvasActionReceipt> {
  const { refuser } = ecriture;
  const prevues = dansLOrdre.map((lu): Remise => ({ paragraphe: lu, aEcrire: ecrituresDeRemiseAZero(lu), faites: new Set(), appliquees: new Set() }));
  const aQuelqueChose = (remise: Remise): boolean => remise.aEcrire.texte || remise.aEcrire.paragraphe;
  // Ce qui fait laisser un paragraphe entier, et ce qui ne fait laisser que son texte : `ecrituresDeRemiseAZero`.
  const laissees = prevues.filter((remise) => remise.aEcrire.laisse !== undefined);
  const gardees = prevues.filter((remise) => !laissees.includes(remise));
  const remises = gardees.filter(aQuelqueChose);
  if (remises.length === 0 && laissees.length > 0) {
    const raisons = [...new Set(laissees.map((laissee) => laissee.aEcrire.laisse))];
    return refuser({
      code: 'FORMAT_TARGET_NOT_WRITABLE',
      message: `format_paragraphs was refused before any write: the ${laissees.length} targeted paragraph(s) with formatting set by hand `
        + `(${nommerLesParagraphes(laissees.map((laissee) => laissee.paragraphe.nodeId))}) cannot be reset (${raisons.join(', ')}). `
        + (raisons.includes('pending_tracked_change') ? 'pending_tracked_change: a pending tracked change that a reset would erase (paragraph formatting, paragraph mark), or that the editor refuses to write over when only text formatting is to be removed (text formatting, a move) — tell the lawyer to accept or reject the pending changes of these paragraphs first. ' : '')
        + (raisons.includes('outline_level_not_restorable') ? 'outline_level_not_restorable: an outline level outside 0–9, which the editor could not give back after the reset. ' : '')
        + 'Nothing was written.',
    });
  }

  // ── La forme du texte : une étape par paragraphe, en plans atomiques directs. Le paragraphe d'une étape que
  // l'éditeur refuse est sauté pour tout l'appel, avec la raison à redire.
  const avecTexte = remises.filter((remise) => remise.aEcrire.texte);
  const plans = await ecrireParPlans(ecriture, avecTexte, {
    famille: 'texte',
    versEtape: () => ({ inline: INLINE_DE_REMISE_A_ZERO }),
    ecritDuTexte: () => true,
    nom: 'text reset',
    etiquette: 'texte',
    dAutresEcrituresSuivent: remises.some((remise) => remise.aEcrire.paragraphe),
  });
  if ('refus' in plans) return plans.refus;

  // ── La forme du paragraphe : une écriture par paragraphe, dans l'ordre du document, et aussitôt après elle la
  // structure qu'elle vient d'effacer.
  const serie = await ecrireLaSerie(ecriture, remises, (remise): EcritureHorsPlan[] => {
    if (!remise.aEcrire.paragraphe) return [];
    const { nodeId } = remise.paragraphe;
    const target = blocDeLEcriture(remise.paragraphe);
    const { sautDePageAvant, niveauDePlan } = remise.aEcrire.structure;
    return [
      { famille: 'paragraphe', etiquette: `reset:${nodeId}`, appel: (options) => paragraphe?.resetDirectFormatting?.({ target }, options), relanceLePeintre: RELANCE_LE_PEINTRE.remiseAZero },
      ...(sautDePageAvant === undefined ? [] : [{
        famille: 'structure',
        etiquette: `saut-de-page:${nodeId}`,
        appel: (options: unknown) => paragraphe?.setFlowOptions?.({ target, pageBreakBefore: sautDePageAvant }, options),
        relanceLePeintre: RELANCE_LE_PEINTRE.sautDePageAvant,
      }]),
      ...(niveauDePlan === undefined ? [] : [{
        famille: 'structure',
        etiquette: `niveau-de-plan:${nodeId}`,
        appel: (options: unknown) => paragraphe?.setOutlineLevel?.({ target, outlineLevel: niveauDePlan }, options),
        relanceLePeintre: RELANCE_LE_PEINTRE.niveauDePlan,
      }]),
    ];
  });
  // Interrompue sur un paragraphe déjà remis à zéro : sa structure n'est pas redonnée. Les écritures qu'il a reçues
  // sont ce qu'une annulation doit défaire.
  const structureNonRedonnee = serie.interrompue === undefined || serie.interrompue.recues === 0
    ? undefined
    : { nodeId: serie.interrompue.travail.paragraphe.nodeId, ecritures: serie.interrompue.recues };
  const cloture = await ecriture.clore();

  // Un paragraphe sauté est compté une fois, comme sauté : il sort des paragraphes jugés et des comptes d'écriture.
  const sautees = remises.filter((remise) => remise.saute !== undefined);
  const jugees = gardees.filter((gardee) => gardee.saute === undefined);
  const aRemettre = remises.filter((remise) => remise.saute === undefined);
  const entieres = aRemettre.filter((remise) => estEntier(remise, FAMILLES_DE_REMISE));
  const paragraphesARemettre = aRemettre.filter((remise) => remise.aEcrire.paragraphe);
  const avecStructure = paragraphesARemettre.filter((remise) => ecrituresDeStructure(remise) > 0);
  const faites = (parmi: readonly Remise[], famille: FamilleDeRemise): number => parmi.filter((remise) => remise.faites.has(famille)).length;
  const avancement = [
    ...(avecTexte.length === 0 ? [] : [`text formatting reset on ${faites(avecTexte, 'texte')} of ${avecTexte.length} paragraphs`]),
    ...(paragraphesARemettre.length === 0 ? [] : [`paragraph formatting reset on ${faites(paragraphesARemettre, 'paragraphe')} of ${paragraphesARemettre.length} paragraphs`]),
    // Une structure non redonnée est une perte : le reçu la dit, paragraphe compté.
    ...(avecStructure.length === 0 ? [] : [`page break before and outline level given back on ${faites(avecStructure, 'structure')} of ${faites(avecStructure, 'paragraphe')} reset paragraphs that carried them`]),
  ].join(', ');
  // Le texte laissé n'est dit que pour un paragraphe dont la forme de paragraphe, elle, est entièrement remise à zéro.
  const texteLaisse = entieres.filter((remise) => remise.aEcrire.texteLaisse === true).map((remise) => remise.paragraphe.nodeId);
  return recuDeLEcritureDeForme({
    verbe: INTENTION,
    reglages: {},
    remiseAZero: {
      // Le compte de ce qui est parti se fait sur le fichier RELU, jamais sur la seule lecture d'avant.
      ...bilanDeLaRemiseAZero(
        entieres.map((remise) => ({ avant: remise.paragraphe, texteLaisse: remise.aEcrire.texteLaisse === true })),
        jugees.map((jugee) => jugee.paragraphe),
        cloture.relecture,
      ),
      ...(texteLaisse.length === 0 ? {} : { texteLaisse }),
      ...(structureNonRedonnee === undefined ? {} : { structureNonRedonnee }),
    },
    // Les paragraphes laissés d'avance sont comptés et nommés par le reçu, sur `laisses`.
    cible: { paragraphes: jugees.map((jugee) => jugee.paragraphe), omis: resolue.omis, nonResolus: resolue.nonResolus, cellules: resolue.cellules },
    ...compterLAvancement(aRemettre, FAMILLES_DE_REMISE, avancement),
    ecrituresPrevues: plans.plans + serie.prevues,
    ...(plans.ecritesSeules === 0 ? {} : { ecritesSeules: plans.ecritesSeules }),
    sautes: sautees.flatMap((sautee) => (sautee.saute === undefined ? [] : [{ nodeId: sautee.paragraphe.nodeId, ...sautee.saute }])),
    ...(laissees.length === 0 ? {} : {
      laisses: laissees.map((laissee) => ({ nodeId: laissee.paragraphe.nodeId, raison: laissee.aEcrire.laisse ?? 'pending_tracked_change', reglages: ['reset'], attributs: [], ecrit: true })),
    }),
    ...cloture,
  });
}
